/**
 * Ambient log context: fields merged into every log line emitted while the
 * provider returns them, without threading them through each call site.
 *
 * The logger package stays runtime-agnostic, so it owns only the hook. A
 * runtime that wants ambient context (the AI service wraps each Marty
 * invocation in an `AsyncLocalStorage` scope) registers a provider that reads
 * its own storage. Where no provider is registered, nothing is added.
 *
 * Per-call fields win over context fields, so an explicit `runId` on a log
 * call is never overwritten by the ambient one.
 */
export type LogContext = Record<string, unknown>;

export type LogContextProvider = () => LogContext | undefined;

let _provider: LogContextProvider | null = null;

export const setLogContextProvider = (
  provider: LogContextProvider | null,
): void => {
  _provider = provider;
};

export const getLogContext = (): LogContext | undefined => {
  if (!_provider) {
    return undefined;
  }
  try {
    return _provider();
  } catch {
    // A broken provider must never take logging down with it.
    return undefined;
  }
};
